import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Archive,
  Check,
  Copy,
  Download,
  ExternalLink,
  Link2,
  Loader2,
  NotebookPen,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Star,
  Tag,
  Upload,
  X,
} from "lucide-react";

import {
  archiveNote,
  batchDownloadNotes,
  batchUploadNoteFiles,
  createNote,
  getNote,
  listNoteTags,
  listNotes,
  searchNotes,
  unarchiveNote,
  updateNote,
} from "../../api/notes";
import NotesEditor from "../Notes/NotesEditor";
import { usePanels } from "../contexts/PanelsContext";
import { useContextMenu } from "../contexts/RightClickMenuContext";

const EMPTY_DRAFT = { title: "", body: "" };
const DEFAULT_NOTE_TITLE = "Untitled note";
const AUTOSAVE_DELAY_MS = 700;

const getApiMessage = (error, fallback) =>
  error?.response?.data?.error || error?.message || fallback;

const formatUpdatedAt = (value) => {
  if (!value) return "Not saved yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not saved yet";

  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const notePreview = (body) => {
  const compact = (body || "").replace(/\s+/g, " ").trim();
  if (!compact) return "No content yet";
  return compact.length > 120 ? `${compact.slice(0, 120)}…` : compact;
};

const makeDraft = (note) => ({
  title: note?.title || DEFAULT_NOTE_TITLE,
  body: note?.body || "",
});

const sameDraft = (a, b) =>
  (a?.title || "") === (b?.title || "") && (a?.body || "") === (b?.body || "");

const normalizeTag = (tag) => tag.trim().toLowerCase();

const normalizeTags = (tags) => {
  const seen = new Set();
  return (tags || []).reduce((acc, tag) => {
    if (typeof tag !== "string") return acc;
    const normalized = normalizeTag(tag);
    if (!normalized || seen.has(normalized)) return acc;

    seen.add(normalized);
    acc.push(normalized);
    return acc;
  }, []);
};

const withDefaultScheme = (url) => {
  const trimmed = (url || "").trim();
  if (!trimmed) return "";
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const trimUrlPunctuation = (value) => {
  let url = (value || "").trim();
  url = url.replace(/[.,;:!?]+$/g, "");
  while (url.endsWith(")") && url.split("(").length <= url.split(")").length) {
    url = url.slice(0, -1);
  }
  return url;
};

const extractUrlsFromMarkdown = (body) => {
  const text = body || "";
  const byUrl = new Map();

  const addUrl = (rawUrl, title = "") => {
    const url = withDefaultScheme(trimUrlPunctuation(rawUrl));
    if (!url || byUrl.has(url)) return;
    byUrl.set(url, {
      url,
      title: title.trim() && title.trim() !== rawUrl ? title.trim() : "",
    });
  };

  const markdownLinkRegex = /\[([^\]]+)\]\(((?:https?:\/\/|www\.)[^\s)]+)\)/gi;
  let match;
  while ((match = markdownLinkRegex.exec(text)) !== null) {
    addUrl(match[2], match[1]);
  }

  const bareUrlRegex = /\b((?:https?:\/\/|www\.)[^\s<>)\]]+)/gi;
  while ((match = bareUrlRegex.exec(text)) !== null) {
    addUrl(match[1]);
  }

  return Array.from(byUrl.values());
};

const getDownloadFilename = (response, fallback) => {
  const disposition = response?.headers?.["content-disposition"] || "";
  const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
  const rawName = match?.[1] || match?.[2];

  if (!rawName) return fallback;

  try {
    return decodeURIComponent(rawName);
  } catch {
    return rawName;
  }
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

const sortNotesForSidebar = (items) =>
  [...items].sort((a, b) => {
    if (Boolean(a.pinned) !== Boolean(b.pinned)) {
      return a.pinned ? -1 : 1;
    }

    return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
  });

const PopUpNotesWindow = () => {
  const { isNotesOpen, setIsNotesOpen } = usePanels();

  const [notes, setNotes] = useState([]);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [activeNote, setActiveNote] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [isTagSuggestionsOpen, setIsTagSuggestionsOpen] = useState(false);
  const [isReferencesOpen, setIsReferencesOpen] = useState(false);
  const [importConflict, setImportConflict] = useState("skip");
  const [includeArchivedInExport, setIncludeArchivedInExport] = useState(false);
  const [batchStatus, setBatchStatus] = useState(null);
  const [activeUtilityPanel, setActiveUtilityPanel] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingNote, setIsLoadingNote] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [exportingFormat, setExportingFormat] = useState(null);
  const [actionFeedback, setActionFeedback] = useState(null);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [saveError, setSaveError] = useState(null);

  const selectedNoteIdRef = useRef(null);
  const importInputRef = useRef(null);
  const titleInputRef = useRef(null);
  const pendingTitleFocusRef = useRef(null);
  const latestDraftRef = useRef(EMPTY_DRAFT);
  const lastSavedDraftRef = useRef(EMPTY_DRAFT);
  const loadListSeqRef = useRef(0);
  const loadNoteSeqRef = useRef(0);
  const saveSeqRef = useRef(0);
  const actionFeedbackTimerRef = useRef(null);

  const showActionFeedback = useCallback((message, type = "success") => {
    if (actionFeedbackTimerRef.current) {
      window.clearTimeout(actionFeedbackTimerRef.current);
    }

    setActionFeedback({ message, type });
    actionFeedbackTimerRef.current = window.setTimeout(() => {
      setActionFeedback(null);
      actionFeedbackTimerRef.current = null;
    }, 2200);
  }, []);

  useEffect(() => {
    return () => {
      if (actionFeedbackTimerRef.current) {
        window.clearTimeout(actionFeedbackTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    selectedNoteIdRef.current = selectedNoteId;
  }, [selectedNoteId]);

  useEffect(() => {
    latestDraftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    if (!activeNote?.note_id || pendingTitleFocusRef.current !== activeNote.note_id) {
      return;
    }

    pendingTitleFocusRef.current = null;
    window.requestAnimationFrame(() => {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    });
  }, [activeNote?.note_id]);

  const statusLabel = useMemo(() => {
    if (saveStatus === "dirty") return "Unsaved changes";
    if (saveStatus === "saving") return "Saving…";
    if (saveStatus === "saved") return "Saved";
    if (saveStatus === "error") return saveError || "Save failed";
    return activeNote ? "Saved" : "";
  }, [activeNote, saveError, saveStatus]);

  const detectedUrls = useMemo(() => extractUrlsFromMarkdown(draft.body), [draft.body]);
  const referenceCount = detectedUrls.length;

  const tagSuggestions = useMemo(() => {
    const currentTags = new Set(activeNote?.tags || []);
    const query = normalizeTag(tagInput);

    return availableTags
      .filter(({ tag }) => {
        if (!tag || currentTags.has(tag)) return false;
        return !query || tag.includes(query);
      })
      .sort((a, b) => {
        const aStarts = query && a.tag.startsWith(query);
        const bStarts = query && b.tag.startsWith(query);
        if (aStarts !== bStarts) return aStarts ? -1 : 1;
        if (a.count !== b.count) return b.count - a.count;
        return a.tag.localeCompare(b.tag);
      })
      .slice(0, 8);
  }, [activeNote, availableTags, tagInput]);

  const setLoadedDraft = useCallback((note) => {
    const nextDraft = makeDraft(note);
    latestDraftRef.current = nextDraft;
    lastSavedDraftRef.current = nextDraft;
    setDraft(nextDraft);
    setTagInput("");
    setIsTagSuggestionsOpen(false);
    setSaveStatus("idle");
    setSaveError(null);
  }, []);

  const refreshTags = useCallback(async () => {
    try {
      const response = await listNoteTags();
      const tags = Array.isArray(response?.data?.tags) ? response.data.tags : [];
      setAvailableTags(tags);
    } catch (err) {
      console.error("Unable to load note tags:", err);
    }
  }, []);

  const refreshNotes = useCallback(async () => {
    const seq = ++loadListSeqRef.current;
    setIsLoadingList(true);
    setError(null);

    try {
      const q = searchTerm.trim();
      const response =
        q.length >= 2
          ? await searchNotes({ q, archived: showArchived, limit: 50 })
          : await listNotes({
              archived: showArchived,
              tags: selectedTags,
              perPage: 50,
            });

      if (seq !== loadListSeqRef.current) return;

      const rawItems = Array.isArray(response?.data?.items)
        ? response.data.items
        : [];
      const filteredItems =
        q.length >= 2 && selectedTags.length > 0
          ? rawItems.filter((note) =>
              selectedTags.every((tag) => (note.tags || []).includes(tag)),
            )
          : rawItems;
      const items = sortNotesForSidebar(filteredItems);
      setNotes(items);

      const currentId = selectedNoteIdRef.current;
      if (!currentId && items.length > 0) {
        setSelectedNoteId(items[0].note_id);
      }
    } catch (err) {
      if (seq !== loadListSeqRef.current) return;
      setError(getApiMessage(err, "Unable to load notes."));
      setNotes([]);
    } finally {
      if (seq === loadListSeqRef.current) {
        setIsLoadingList(false);
      }
    }
  }, [searchTerm, selectedTags, showArchived]);

  useEffect(() => {
    if (!isNotesOpen) return;
    refreshTags();
  }, [isNotesOpen, refreshTags]);

  useEffect(() => {
    if (!isNotesOpen) return undefined;

    const delay = searchTerm.trim().length > 0 ? 250 : 0;
    const timer = window.setTimeout(() => {
      refreshNotes();
    }, delay);

    return () => window.clearTimeout(timer);
  }, [isNotesOpen, refreshNotes, searchTerm]);

  useEffect(() => {
    if (!isNotesOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsNotesOpen(false);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isNotesOpen, setIsNotesOpen]);

  useEffect(() => {
    if (!selectedNoteId) {
      setActiveNote(null);
      setLoadedDraft(null);
      return;
    }

    const seq = ++loadNoteSeqRef.current;
    setIsLoadingNote(true);
    setError(null);

    getNote(selectedNoteId)
      .then((response) => {
        if (seq !== loadNoteSeqRef.current) return;
        const note = response?.data?.note || null;
        setActiveNote(note);
        if (note) {
          setLoadedDraft(note);
        }
      })
      .catch((err) => {
        if (seq !== loadNoteSeqRef.current) return;
        setError(getApiMessage(err, "Unable to load note."));
      })
      .finally(() => {
        if (seq === loadNoteSeqRef.current) {
          setIsLoadingNote(false);
        }
      });
  }, [selectedNoteId, setLoadedDraft]);

  const saveDraft = useCallback(
    async (noteId, draftToSave) => {
      if (!noteId) return;
      if (sameDraft(draftToSave, lastSavedDraftRef.current)) return;

      const title = draftToSave.title.trim();
      if (!title) {
        setSaveStatus("error");
        setSaveError("Title is required");
        return;
      }

      const fields = {};
      if (title !== lastSavedDraftRef.current.title) fields.title = title;
      if (draftToSave.body !== lastSavedDraftRef.current.body) {
        fields.body = draftToSave.body;
      }
      if (Object.keys(fields).length === 0) {
        latestDraftRef.current = lastSavedDraftRef.current;
        setDraft(lastSavedDraftRef.current);
        setSaveStatus("saved");
        return;
      }

      const seq = ++saveSeqRef.current;
      setSaveStatus("saving");
      setSaveError(null);

      try {
        const response = await updateNote(noteId, fields);
        if (seq !== saveSeqRef.current) return;

        const savedNote = response?.data?.note;
        if (!savedNote) {
          throw new Error("Update note response did not include a note.");
        }
        const savedDraft = makeDraft(savedNote);
        const draftStillCurrent = sameDraft(latestDraftRef.current, draftToSave);
        lastSavedDraftRef.current = savedDraft;
        if (draftStillCurrent) {
          latestDraftRef.current = savedDraft;
          setDraft(savedDraft);
        }
        setActiveNote((prev) =>
          prev?.note_id === noteId ? { ...prev, ...savedNote } : prev,
        );
        setNotes((prev) =>
          prev.map((note) =>
            note.note_id === noteId ? { ...note, ...savedNote } : note,
          ),
        );
        setSaveStatus(draftStillCurrent ? "saved" : "dirty");
      } catch (err) {
        if (seq !== saveSeqRef.current) return;
        setSaveStatus("error");
        setSaveError(getApiMessage(err, "Save failed"));
      }
    },
    [],
  );

  useEffect(() => {
    if (!selectedNoteId || selectedNoteId !== activeNote?.note_id || isLoadingNote) {
      return undefined;
    }
    if (sameDraft(draft, lastSavedDraftRef.current)) return undefined;

    setSaveStatus("dirty");
    setSaveError(null);

    const timer = window.setTimeout(() => {
      saveDraft(selectedNoteId, draft);
    }, AUTOSAVE_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [activeNote?.note_id, draft, isLoadingNote, saveDraft, selectedNoteId]);

  const handleCreateNote = async () => {
    if (isCreating) return;
    setActiveUtilityPanel(null);
    setIsCreating(true);
    setError(null);

    try {
      const response = await createNote({
        title: DEFAULT_NOTE_TITLE,
        body: "",
      });
      const note = response?.data?.note;
      if (!note) throw new Error("Create note response did not include a note.");

      setSearchTerm("");
      setShowArchived(false);
      setNotes((prev) =>
        sortNotesForSidebar([
          note,
          ...prev.filter((n) => n.note_id !== note.note_id),
        ]),
      );
      setActiveNote(note);
      setLoadedDraft(note);
      setSelectedNoteId(note.note_id);
    } catch (err) {
      setError(getApiMessage(err, "Unable to create note."));
    } finally {
      setIsCreating(false);
    }
  };

  const resolveNoteFromPayload = useCallback(
    (payload) => {
      const noteId = payload?.note_id;
      if (!noteId) return null;

      if (activeNote?.note_id === noteId) {
        return {
          ...activeNote,
          title: latestDraftRef.current.title,
          body: latestDraftRef.current.body,
        };
      }

      return notes.find((note) => note.note_id === noteId) || null;
    },
    [activeNote, notes],
  );

  const focusTitleInput = useCallback(() => {
    window.requestAnimationFrame(() => {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    });
  }, []);

  const selectContextNote = useCallback((payload) => {
    if (!payload?.note_id) return;
    setActiveUtilityPanel(null);
    setSelectedNoteId(payload.note_id);
  }, []);

  const renameContextNote = useCallback(
    (payload) => {
      if (!payload?.note_id) return;

      pendingTitleFocusRef.current = payload.note_id;
      setActiveUtilityPanel(null);
      setSelectedNoteId(payload.note_id);
      if (selectedNoteIdRef.current === payload.note_id) {
        focusTitleInput();
      }
    },
    [focusTitleInput],
  );

  const handleTogglePin = useCallback(
    async (payload) => {
      const note = resolveNoteFromPayload(payload);
      if (!note) return;

      setError(null);
      try {
        const response = await updateNote(note.note_id, { pinned: !note.pinned });
        const savedNote = response?.data?.note;
        if (!savedNote) throw new Error("Pin note response did not include a note.");

        setNotes((prev) =>
          sortNotesForSidebar(
            prev.map((item) =>
              item.note_id === savedNote.note_id ? { ...item, ...savedNote } : item,
            ),
          ),
        );
        setActiveNote((prev) =>
          prev?.note_id === savedNote.note_id ? { ...prev, ...savedNote } : prev,
        );
        showActionFeedback(savedNote.pinned ? "Pinned" : "Unpinned");
      } catch (err) {
        setError(getApiMessage(err, "Unable to update pinned state."));
      }
    },
    [resolveNoteFromPayload, showActionFeedback],
  );

  const handleToggleActivePin = useCallback(() => {
    if (!activeNote?.note_id) return;
    handleTogglePin({ note_id: activeNote.note_id });
  }, [activeNote?.note_id, handleTogglePin]);

  const updateActiveNoteTags = useCallback(
    async (nextTags) => {
      if (!activeNote?.note_id || isSavingTags) return;

      const tags = normalizeTags(nextTags).slice(0, 50);
      setIsSavingTags(true);
      setError(null);

      try {
        const response = await updateNote(activeNote.note_id, { tags });
        const savedNote = response?.data?.note;
        if (!savedNote) throw new Error("Tag update response did not include a note.");

        setActiveNote((prev) =>
          prev?.note_id === savedNote.note_id ? { ...prev, ...savedNote } : prev,
        );
        setNotes((prev) =>
          sortNotesForSidebar(
            prev.map((note) =>
              note.note_id === savedNote.note_id ? { ...note, ...savedNote } : note,
            ),
          ),
        );
        showActionFeedback("Tags updated");
        await refreshTags();
        await refreshNotes();
      } catch (err) {
        setError(getApiMessage(err, "Unable to update tags."));
      } finally {
        setIsSavingTags(false);
      }
    },
    [activeNote, isSavingTags, refreshNotes, refreshTags, showActionFeedback],
  );

  const handleAddTag = useCallback(() => {
    const tag = normalizeTag(tagInput);
    if (!tag || !activeNote) return;

    setTagInput("");
    setIsTagSuggestionsOpen(false);
    if ((activeNote.tags || []).includes(tag)) return;
    updateActiveNoteTags([...(activeNote.tags || []), tag]);
  }, [activeNote, tagInput, updateActiveNoteTags]);

  const handleAddExistingTag = useCallback(
    (tag) => {
      const normalized = normalizeTag(tag);
      if (!normalized || !activeNote) return;

      setTagInput("");
      setIsTagSuggestionsOpen(false);
      if ((activeNote.tags || []).includes(normalized)) return;
      updateActiveNoteTags([...(activeNote.tags || []), normalized]);
    },
    [activeNote, updateActiveNoteTags],
  );

  const handleRemoveTag = useCallback(
    (tag) => {
      if (!activeNote) return;
      updateActiveNoteTags((activeNote.tags || []).filter((item) => item !== tag));
    },
    [activeNote, updateActiveNoteTags],
  );

  const handleTagInputKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" || event.key === ",") {
        event.preventDefault();
        handleAddTag();
        return;
      }

      if (event.key === "Escape") {
        setIsTagSuggestionsOpen(false);
        return;
      }

      if (event.key === "Backspace" && !tagInput && activeNote?.tags?.length) {
        handleRemoveTag(activeNote.tags[activeNote.tags.length - 1]);
      }
    },
    [activeNote, handleAddTag, handleRemoveTag, tagInput],
  );

  const toggleTagFilter = useCallback((tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((item) => item !== tag) : [...prev, tag],
    );
  }, []);

  const handleCopyMarkdown = useCallback(
    async (payload) => {
      const note = resolveNoteFromPayload(payload);
      if (!note) return;

      try {
        await navigator.clipboard.writeText(note.body || "");
        showActionFeedback("Copied markdown");
      } catch (err) {
        setError(getApiMessage(err, "Unable to copy note markdown."));
      }
    },
    [resolveNoteFromPayload, showActionFeedback],
  );

  const handleArchiveNote = useCallback(
    async (payload) => {
      const note = resolveNoteFromPayload(payload);
      if (!note || isArchiving) return;

      const isArchived = Boolean(note.archived);
      const action = isArchived ? unarchiveNote : archiveNote;

      setIsArchiving(true);
      setError(null);

      try {
        await action(note.note_id);
        const nextArchived = !isArchived;
        const updatedNote = { ...note, archived: nextArchived };
        const remainingNotes = notes.filter((item) => item.note_id !== note.note_id);
        setNotes(remainingNotes);
        setActiveNote((prev) =>
          prev?.note_id === note.note_id ? { ...prev, archived: nextArchived } : prev,
        );
        if (selectedNoteIdRef.current === note.note_id) {
          setSelectedNoteId(note.note_id);
          lastSavedDraftRef.current = makeDraft(updatedNote);
        }
        showActionFeedback(nextArchived ? "Archived" : "Restored");
      } catch (err) {
        setError(getApiMessage(err, "Unable to update archive state."));
      } finally {
        setIsArchiving(false);
      }
    },
    [isArchiving, notes, resolveNoteFromPayload, showActionFeedback],
  );

  const handleToggleArchive = async () => {
    if (!activeNote) return;
    await handleArchiveNote({ note_id: activeNote.note_id });
  };

  const handleBackdropMouseDown = (event) => {
    if (event.target === event.currentTarget) {
      setIsNotesOpen(false);
    }
  };

  const handleManualRefresh = () => {
    refreshNotes();
  };

  const handleImportClick = () => {
    importInputRef.current?.click();
  };

  const handleOpenBatchPanel = () => {
    setActiveUtilityPanel("batch");
  };

  const handleImportFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0 || isImporting) return;

    setIsImporting(true);
    setBatchStatus(null);
    setError(null);

    try {
      const response = await batchUploadNoteFiles({
        files,
        onConflict: importConflict,
      });
      const result = response?.data || {};
      const errors = Array.isArray(result.errors) ? result.errors.length : 0;
      const parts = [
        `${result.created || 0} created`,
        `${result.updated || 0} updated`,
        `${result.skipped || 0} skipped`,
      ];
      if (errors > 0) parts.push(`${errors} errors`);

      const importMessage = `Import complete: ${parts.join(", ")}.`;
      setBatchStatus({
        type: errors > 0 ? "warning" : "success",
        message: importMessage,
      });
      showActionFeedback(importMessage, errors > 0 ? "warning" : "success");
      await refreshTags();
      await refreshNotes();
    } catch (err) {
      const message = getApiMessage(err, "Unable to import notes.");
      setBatchStatus({ type: "error", message });
      setError(message);
    } finally {
      setIsImporting(false);
      event.target.value = "";
    }
  };

  const handleExport = async (format) => {
    if (exportingFormat) return;

    setExportingFormat(format);
    setBatchStatus(null);
    setError(null);

    try {
      const response = await batchDownloadNotes({
        format,
        includeArchived: includeArchivedInExport,
      });
      const fallback = format === "zip" ? "notes-export.zip" : "notes-export.json";
      downloadBlob(response?.data, getDownloadFilename(response, fallback));
      const exportMessage = `Exported ${format.toUpperCase()} notes.`;
      setBatchStatus({
        type: "success",
        message: exportMessage,
      });
      showActionFeedback(exportMessage);
    } catch (err) {
      const message = getApiMessage(err, "Unable to export notes.");
      setBatchStatus({ type: "error", message });
      setError(message);
    } finally {
      setExportingFormat(null);
    }
  };

  useContextMenu("notes-note-row", [
    {
      label: "Open note",
      icon: NotebookPen,
      action: selectContextNote,
    },
    {
      label: "Rename",
      icon: Pencil,
      action: renameContextNote,
    },
    {
      getLabel: (payload) =>
        resolveNoteFromPayload(payload)?.pinned ? "Unpin note" : "Pin note",
      icon: Star,
      action: handleTogglePin,
    },
    {
      label: "Copy markdown",
      icon: Copy,
      action: handleCopyMarkdown,
    },
    {
      getLabel: (payload) =>
        resolveNoteFromPayload(payload)?.archived ? "Restore note" : "Archive note",
      getIcon: (payload) =>
        resolveNoteFromPayload(payload)?.archived ? RotateCcw : Archive,
      action: handleArchiveNote,
    },
  ]);

  if (!isNotesOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-mainBg/70 p-2 backdrop-blur-sm md:p-3"
      onMouseDown={handleBackdropMouseDown}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="notes-modal-title"
        className="flex h-[min(920px,calc(100vh-1rem))] w-[min(1440px,calc(100vw-1rem))] flex-col overflow-hidden rounded-xl border border-surfaceBorder bg-surface/95 shadow-login-panel backdrop-blur-md"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-dropdownDivider bg-surface/80 px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-surfaceBorder bg-mainBg/35">
              <NotebookPen strokeWidth={1.5} className="h-5 w-5 text-velBlue" />
            </span>
            <div className="min-w-0">
              <h2
                id="notes-modal-title"
                className="truncate text-sm font-medium tracking-wide text-mainText"
              >
                Notes
              </h2>
              <p className="truncate text-xs text-mutedText">
                Private markdown notebook
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsNotesOpen(false)}
              className="rounded-lg border border-transparent p-2 text-mutedText transition hover:border-velBlueSecond/25 hover:bg-btnHover hover:text-mainText"
              aria-label="Close notes"
            >
              <X strokeWidth={1.5} className="h-5 w-5" />
            </button>
          </div>
        </header>

        <div className="flex min-h-0 flex-1">
          <aside className="flex w-[320px] shrink-0 flex-col border-r border-dropdownDivider bg-mainBg/30">
            <div className="space-y-3 border-b border-dropdownDivider p-3">
              <button
                type="button"
                onClick={handleCreateNote}
                disabled={isCreating}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-velBlue/30 bg-velBlue/15 px-3 py-2 text-sm font-normal text-velBlue transition hover:bg-velBlue/20 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isCreating ? (
                  <Loader2 strokeWidth={1.5} className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus strokeWidth={1.5} className="h-4 w-4" />
                )}
                New note
              </button>

              <div className="flex items-center gap-2">
                <div className="relative min-w-0 flex-1">
                  <Search
                    strokeWidth={1.5}
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mutedText/70"
                  />
                  <input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search notes"
                    className="w-full rounded-lg border border-surfaceBorder bg-mainBg/80 py-2 pl-9 pr-3 text-sm text-mainText outline-none transition placeholder:text-mutedText/60 focus:border-velBlue/50 focus:ring-2 focus:ring-inputFocus/40"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => setShowArchived((value) => !value)}
                  className={[
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition",
                    showArchived
                      ? "border-velBlue/30 bg-velBlue/15 text-velBlue"
                      : "border-surfaceBorder text-mutedText hover:border-velBlueSecond/25 hover:bg-btnHover hover:text-mainText",
                  ].join(" ")}
                  aria-label={showArchived ? "Show active notes" : "Show archived notes"}
                  title={showArchived ? "Show active notes" : "Show archived notes"}
                >
                  {showArchived ? (
                    <NotebookPen strokeWidth={1.5} className="h-4 w-4" />
                  ) : (
                    <Archive strokeWidth={1.5} className="h-4 w-4" />
                  )}
                </button>
              </div>

              {availableTags.length > 0 && (
                <div className="flex max-h-20 flex-wrap gap-1 overflow-y-auto pr-1">
                  {selectedTags.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedTags([])}
                      className="rounded-full border border-velBlue/25 bg-velBlue/10 px-2 py-1 text-[11px] text-velBlue transition hover:bg-velBlue/15"
                    >
                      Clear
                    </button>
                  )}
                  {availableTags.map(({ tag, count }) => {
                    const selected = selectedTags.includes(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => toggleTagFilter(tag)}
                        className={[
                          "rounded-full border px-2 py-1 text-[11px] transition",
                          selected
                            ? "border-velBlue/30 bg-velBlue/10 text-velBlue"
                            : "border-surfaceBorder text-mutedText hover:bg-btnHover hover:text-mainText",
                        ].join(" ")}
                      >
                        {tag}
                        <span className="ml-1 opacity-60">{count}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between px-3 py-2 text-xs text-mutedText">
              <span>{notes.length} notes</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={handleOpenBatchPanel}
                  className={[
                    "rounded-md p-1 transition hover:bg-btnHover hover:text-mainText",
                    activeUtilityPanel === "batch" ? "bg-velBlue/15 text-velBlue" : "",
                  ].join(" ")}
                  aria-label="Import or export notes"
                  title="Import / Export"
                >
                  <Download strokeWidth={1.5} className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleManualRefresh}
                  className="rounded-md p-1 transition hover:bg-btnHover hover:text-mainText"
                  aria-label="Refresh notes"
                  title="Refresh notes"
                >
                  <RotateCcw
                    strokeWidth={1.5}
                    className={[
                      "h-4 w-4",
                      isLoadingList ? "animate-spin text-velBlue" : "",
                    ].join(" ")}
                  />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              {isLoadingList && notes.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-mutedText">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading notes…
                </div>
              ) : notes.length === 0 ? (
                <div className="rounded-lg border border-dashed border-surfaceBorder p-4 text-center text-sm text-mutedText">
                  {searchTerm.trim().length >= 2
                    ? "No notes match your search."
                    : showArchived
                      ? "No archived notes yet."
                      : "No notes yet. Create your first note."}
                </div>
              ) : (
                <ul className="space-y-1.5">
                  {notes.map((note) => {
                    const isSelected = note.note_id === selectedNoteId;
                    return (
                      <li key={note.note_id}>
                        <button
                          type="button"
                          onClick={() => {
                            setActiveUtilityPanel(null);
                            setSelectedNoteId(note.note_id);
                          }}
                          data-context-menu="notes-note-row"
                          data-context-payload={JSON.stringify({
                            note_id: note.note_id,
                          })}
                          title="Right-click for note actions"
                          className={[
                            "w-full rounded-lg border px-3 py-2 text-left transition",
                            isSelected
                              ? "border-velBlue/30 bg-velBlue/10"
                              : "border-transparent hover:border-velBlueSecond/25 hover:bg-btnHover",
                          ].join(" ")}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <span
                              className={[
                                "line-clamp-1 text-sm font-medium",
                                isSelected ? "text-velBlue" : "text-mainText",
                              ].join(" ")}
                            >
                              {note.title || DEFAULT_NOTE_TITLE}
                            </span>
                            {note.pinned && (
                              <span className="rounded-full border border-velBlue/25 bg-velBlue/10 px-1.5 py-0.5 text-[10px] text-velBlue">
                                pinned
                              </span>
                            )}
                          </div>
                          <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-mutedText">
                            {notePreview(note.body)}
                          </p>
                          <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-mutedText/80">
                            <span>{formatUpdatedAt(note.updated_at)}</span>
                            {note.tags?.length > 0 && (
                              <span className="truncate">
                                {note.tags.slice(0, 2).join(", ")}
                              </span>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </aside>

          <main className="flex min-w-0 flex-1 flex-col bg-mainBg/10">
            {error && (
              <div className="border-b border-logoutColor/20 bg-logoutColor/10 px-4 py-2 text-sm text-logoutColor">
                {error}
              </div>
            )}

            {activeUtilityPanel === "batch" ? (
              <div className="flex flex-1 items-center justify-center p-8">
                <div className="w-full max-w-xl rounded-xl border border-surfaceBorder bg-mainBg/35 p-5 shadow-login-panel">
                  <input
                    ref={importInputRef}
                    type="file"
                    multiple
                    accept=".json,.zip,.csv,.md,application/json,application/zip,text/csv,text/markdown,text/plain"
                    className="hidden"
                    onChange={handleImportFiles}
                  />

                  <div className="mb-5 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-base font-medium text-mainText">
                        Import / Export
                      </h3>
                      <p className="mt-1 text-sm text-mutedText">
                        Move notes in or out as JSON, ZIP markdown, CSV, or markdown files.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setActiveUtilityPanel(null)}
                      className="rounded-lg border border-transparent p-2 text-mutedText transition hover:border-velBlueSecond/25 hover:bg-btnHover hover:text-mainText"
                      aria-label="Close import export panel"
                    >
                      <X strokeWidth={1.5} className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="rounded-lg border border-surfaceBorder bg-surface/50 p-4">
                      <div className="mb-3 flex items-center gap-2 text-sm font-medium text-mainText">
                        <Upload strokeWidth={1.5} className="h-4 w-4 text-mutedText" />
                        Import
                      </div>
                      <p className="mb-3 text-xs leading-relaxed text-mutedText">
                        Upload one or more `.json`, `.zip`, `.csv`, or `.md` files.
                      </p>
                      <select
                        value={importConflict}
                        onChange={(event) => setImportConflict(event.target.value)}
                        className="mb-3 w-full rounded-lg border border-surfaceBorder bg-mainBg px-3 py-2 text-sm text-mutedText outline-none focus:border-velBlue/50"
                        aria-label="Import conflict behavior"
                      >
                        <option value="skip">Skip existing notes</option>
                        <option value="replace">Replace existing notes</option>
                        <option value="error">Stop on conflict</option>
                      </select>
                      <button
                        type="button"
                        onClick={handleImportClick}
                        disabled={isImporting}
                        className="flex w-full items-center justify-center gap-2 rounded-lg border border-surfaceBorder bg-mainBg/45 px-3 py-2 text-sm text-mainText transition hover:border-velBlueSecond/25 hover:bg-btnHover disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isImporting ? (
                          <Loader2 strokeWidth={1.5} className="h-4 w-4 animate-spin" />
                        ) : (
                          <Upload strokeWidth={1.5} className="h-4 w-4" />
                        )}
                        Choose files
                      </button>
                    </div>

                    <div className="rounded-lg border border-surfaceBorder bg-surface/50 p-4">
                      <div className="mb-3 flex items-center gap-2 text-sm font-medium text-mainText">
                        <Download strokeWidth={1.5} className="h-4 w-4 text-mutedText" />
                        Export
                      </div>
                      <p className="mb-3 text-xs leading-relaxed text-mutedText">
                        Download your notes as API JSON or a ZIP of markdown files.
                      </p>
                      <label className="mb-3 flex items-center gap-2 text-xs text-mutedText">
                        <input
                          type="checkbox"
                          checked={includeArchivedInExport}
                          onChange={(event) =>
                            setIncludeArchivedInExport(event.target.checked)
                          }
                          className="h-3.5 w-3.5 rounded border-surfaceBorder accent-velBlueSecond"
                        />
                        Include archived notes
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => handleExport("json")}
                          disabled={Boolean(exportingFormat)}
                          className="flex items-center justify-center gap-2 rounded-lg border border-surfaceBorder px-3 py-2 text-sm text-mutedText transition hover:border-velBlueSecond/25 hover:bg-btnHover hover:text-mainText disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {exportingFormat === "json" ? (
                            <Loader2 strokeWidth={1.5} className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download strokeWidth={1.5} className="h-4 w-4" />
                          )}
                          JSON
                        </button>
                        <button
                          type="button"
                          onClick={() => handleExport("zip")}
                          disabled={Boolean(exportingFormat)}
                          className="flex items-center justify-center gap-2 rounded-lg border border-surfaceBorder px-3 py-2 text-sm text-mutedText transition hover:border-velBlueSecond/25 hover:bg-btnHover hover:text-mainText disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {exportingFormat === "zip" ? (
                            <Loader2 strokeWidth={1.5} className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download strokeWidth={1.5} className="h-4 w-4" />
                          )}
                          ZIP
                        </button>
                      </div>
                    </div>
                  </div>

                  {batchStatus && (
                    <p
                      className={[
                        "mt-4 rounded-lg px-3 py-2 text-sm leading-relaxed",
                        batchStatus.type === "error"
                          ? "bg-logoutColor/10 text-logoutColor"
                          : batchStatus.type === "warning"
                            ? "bg-yellow-500/10 text-yellow-500"
                            : "bg-velBlue/10 text-velBlue",
                      ].join(" ")}
                    >
                      {batchStatus.message}
                    </p>
                  )}
                </div>
              </div>
            ) : !selectedNoteId && !activeNote ? (
              <div className="flex flex-1 items-center justify-center p-8 text-center">
                <div className="max-w-sm">
                  <NotebookPen
                    strokeWidth={1}
                    className="mx-auto mb-4 h-12 w-12 text-mutedText/40"
                  />
                  <h3 className="text-base font-medium text-mainText">
                    Select or create a note
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-mutedText">
                    Your notebook lives here. Use the sidebar to create a note,
                    then write markdown in the content area.
                  </p>
                </div>
              </div>
            ) : isLoadingNote && !activeNote ? (
              <div className="flex flex-1 items-center justify-center text-sm text-mutedText">
                <Loader2 className="mr-2 h-4 w-4 animate-spin text-mutedText" />
                Loading note…
              </div>
            ) : (
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="flex items-center gap-3 border-b border-dropdownDivider p-4">
                  <input
                    ref={titleInputRef}
                    value={draft.title}
                    onChange={(event) =>
                      setDraft((prev) => ({ ...prev, title: event.target.value }))
                    }
                    className="min-w-0 flex-1 rounded-lg border border-transparent bg-transparent px-2 py-1 text-xl font-medium text-mainText outline-none transition placeholder:text-mutedText/50 hover:border-surfaceBorder focus:border-velBlue/40 focus:bg-mainBg/40 focus:ring-2 focus:ring-inputFocus/30"
                    placeholder="Untitled note"
                    maxLength={200}
                  />

                  <div className="flex items-center gap-2">
                    {isLoadingNote && activeNote && (
                      <span className="hidden items-center gap-1 text-xs text-mutedText md:flex">
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-mutedText" />
                        Loading
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={handleToggleActivePin}
                      className={[
                        "flex h-9 w-9 items-center justify-center rounded-lg border transition",
                        activeNote?.pinned
                          ? "border-velBlue/30 bg-velBlue/15 text-velBlue"
                          : "border-surfaceBorder text-mutedText hover:border-velBlueSecond/25 hover:bg-btnHover hover:text-mainText",
                      ].join(" ")}
                      aria-label={activeNote?.pinned ? "Unpin note" : "Pin note"}
                      title={activeNote?.pinned ? "Unpin note" : "Pin note"}
                    >
                      <Star
                        strokeWidth={1.5}
                        className="h-4 w-4"
                        fill={activeNote?.pinned ? "currentColor" : "none"}
                      />
                    </button>

                    <button
                      type="button"
                      onClick={() => setIsReferencesOpen((value) => !value)}
                      className={[
                        "flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm transition",
                        isReferencesOpen
                          ? "border-velBlue/30 bg-velBlue/15 text-velBlue"
                          : "border-surfaceBorder text-mutedText hover:border-velBlueSecond/25 hover:bg-btnHover hover:text-mainText",
                      ].join(" ")}
                      aria-expanded={isReferencesOpen}
                      title="URLs and citations"
                    >
                      <Link2 strokeWidth={1.5} className="h-4 w-4" />
                      {referenceCount > 0 && (
                        <span className="text-xs">{referenceCount}</span>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={handleToggleArchive}
                      disabled={isArchiving}
                      className="flex items-center gap-2 rounded-lg border border-surfaceBorder px-3 py-2 text-sm text-mutedText transition hover:border-velBlueSecond/25 hover:bg-btnHover hover:text-mainText disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isArchiving ? (
                        <Loader2 strokeWidth={1.5} className="h-4 w-4 animate-spin" />
                      ) : activeNote?.archived ? (
                        <RotateCcw strokeWidth={1.5} className="h-4 w-4" />
                      ) : (
                        <Archive strokeWidth={1.5} className="h-4 w-4" />
                      )}
                      <span className="hidden sm:inline">
                        {activeNote?.archived ? "Restore" : "Archive"}
                      </span>
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 border-b border-dropdownDivider px-4 py-2">
                  <span className="flex items-center gap-1 text-xs text-mutedText">
                    <Tag strokeWidth={1.5} className="h-3.5 w-3.5" />
                    Tags
                  </span>

                  {(activeNote?.tags || []).map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 rounded-full border border-velBlue/25 bg-velBlue/10 px-2 py-1 text-xs text-velBlue"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        disabled={isSavingTags}
                        className="rounded-full p-0.5 transition hover:bg-btnHover disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label={`Remove ${tag} tag`}
                      >
                        <X strokeWidth={1.5} className="h-3 w-3" />
                      </button>
                    </span>
                  ))}

                  <div className="relative min-w-[140px] flex-1">
                    <input
                      value={tagInput}
                      onChange={(event) => {
                        setTagInput(event.target.value);
                        setIsTagSuggestionsOpen(true);
                      }}
                      onFocus={() => setIsTagSuggestionsOpen(true)}
                      onKeyDown={handleTagInputKeyDown}
                      onBlur={() => {
                        handleAddTag();
                        window.setTimeout(() => setIsTagSuggestionsOpen(false), 100);
                      }}
                      disabled={isSavingTags}
                      placeholder={(activeNote?.tags || []).length ? "Add tag" : "Add a tag"}
                      className="w-full rounded-lg border border-transparent bg-transparent px-2 py-1 text-xs text-mainText outline-none transition placeholder:text-mutedText/50 hover:border-surfaceBorder focus:border-velBlue/40 focus:bg-mainBg/40 disabled:cursor-not-allowed disabled:opacity-60"
                      maxLength={40}
                      aria-label="Add note tag"
                      aria-expanded={isTagSuggestionsOpen && tagSuggestions.length > 0}
                    />

                    {isTagSuggestionsOpen && tagSuggestions.length > 0 && (
                      <div className="absolute left-0 top-full z-[60] mt-1 max-h-48 w-full min-w-[220px] overflow-y-auto rounded-lg border border-surfaceBorder bg-dropdownBg/95 p-1 shadow-login-panel backdrop-blur-md">
                        {tagSuggestions.map(({ tag, count }) => (
                          <button
                            key={tag}
                            type="button"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => handleAddExistingTag(tag)}
                            className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left text-xs text-mutedText transition hover:bg-dropdownItemHover hover:text-mainText"
                          >
                            <span className="truncate">{tag}</span>
                            <span className="shrink-0 text-[10px] text-mutedText/70">
                              {count}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {isSavingTags && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-mutedText" />
                  )}
                </div>

                {isReferencesOpen && (
                  <div className="max-h-72 overflow-y-auto border-b border-dropdownDivider bg-mainBg/20 px-4 py-3">
                    <div className="space-y-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 text-sm font-medium text-mainText">
                            <Link2 strokeWidth={1.5} className="h-4 w-4 text-velBlue" />
                            Detected URLs
                          </div>
                          <span className="text-xs text-mutedText">
                            {detectedUrls.length} found
                          </span>
                        </div>

                        <p className="rounded-lg border border-surfaceBorder bg-mainBg/35 px-3 py-2 text-xs leading-relaxed text-mutedText">
                          Add or edit links directly in the note body. Markdown links like
                          {" "}
                          <span className="font-mono text-mainText/80">[title](url)</span>
                          {" "}
                          will use their markdown title here.
                        </p>

                        {detectedUrls.length > 0 ? (
                          <ul className="space-y-2">
                            {detectedUrls.map((entry) => (
                              <li
                                key={entry.url}
                                className="rounded-lg border border-surfaceBorder bg-surface/45 px-3 py-2"
                              >
                                <a
                                  href={entry.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex min-w-0 items-center gap-1.5 text-sm text-mainText transition hover:text-velBlue"
                                >
                                  <span className="truncate">
                                    {entry.title || entry.url}
                                  </span>
                                  <ExternalLink
                                    strokeWidth={1.5}
                                    className="h-3.5 w-3.5 shrink-0"
                                  />
                                </a>
                                {entry.title && (
                                  <p className="mt-0.5 truncate text-xs text-mutedText">
                                    {entry.url}
                                  </p>
                                )}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="rounded-lg border border-dashed border-surfaceBorder px-3 py-4 text-center text-sm text-mutedText">
                            No URLs detected in this note yet.
                          </p>
                        )}
                    </div>
                  </div>
                )}

                <NotesEditor
                  value={draft.body}
                  onChange={(body) => setDraft((prev) => ({ ...prev, body }))}
                  placeholder="Start writing markdown…"
                />

                <footer className="flex items-center justify-between border-t border-dropdownDivider px-4 py-2 text-xs text-mutedText">
                  <span>{draft.body.length.toLocaleString()} / 100,000 chars</span>
                  <div className="flex min-w-0 items-center gap-3">
                    {actionFeedback && (
                      <span
                        className={[
                          "inline-flex max-w-[260px] items-center gap-1 truncate rounded-full border px-2 py-1",
                          actionFeedback.type === "error"
                            ? "border-logoutColor/25 bg-logoutColor/10 text-logoutColor"
                            : actionFeedback.type === "warning"
                              ? "border-yellow-500/25 bg-yellow-500/10 text-yellow-500"
                              : "border-velBlue/25 bg-velBlue/10 text-velBlue",
                        ].join(" ")}
                      >
                        <Check className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{actionFeedback.message}</span>
                      </span>
                    )}
                    {statusLabel && (
                      <span
                        className={[
                          "inline-flex items-center gap-1 rounded-full border px-2 py-1",
                          saveStatus === "error"
                            ? "border-logoutColor/25 bg-logoutColor/10 text-logoutColor"
                            : "border-surfaceBorder bg-mainBg/30 text-mutedText",
                        ].join(" ")}
                      >
                        {saveStatus === "saving" ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : saveStatus === "saved" || saveStatus === "idle" ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : null}
                        {statusLabel}
                      </span>
                    )}
                    <span>Updated {formatUpdatedAt(activeNote?.updated_at)}</span>
                  </div>
                </footer>
              </div>
            )}
          </main>
        </div>
      </section>
    </div>
  );
};

export default PopUpNotesWindow;
